import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import './MediaToolKit.css';
import SearchScreenNav from '../components/SearchScreenNav';

// Reusable animated stat circle
const StatCircle = ({ percentage }) => {
  const [animated, setAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimated(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // SVG circle math
  const size = 190;
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = animated ? circumference - (percentage / 100) * circumference : circumference;

  return (
    <div className="mediakit-stat-circle" ref={ref}>
      <svg className="mediakit-stat-svg" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background ring */}
        <circle
          className="mediakit-stat-ring-bg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress ring */}
        <circle
          className="mediakit-stat-ring-fg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <span className="mediakit-stat-number">{percentage}</span>
    </div>
  );
};

const MediaToolKit = () => {
  return (
    <div className="mediakit-page-container">
      <Helmet>
        <title>Media Tool Kit | LGBT PridePath</title>
        <meta name="description" content="LGBT PridePath media kit — press resources, statistics, brand assets, and contact information for media inquiries about LGBTQ+ relocation and protections research." />
        <link rel="canonical" href="https://www.lgbtpridepath.org/media-tool-kit" />
      </Helmet>

      {/* Custom navigation matching About page pattern */}
      <SearchScreenNav />

      {/* ============================================ */}
      {/* HERO SECTION                                  */}
      {/* ============================================ */}
      <section className="mediakit-hero">
        {/* Hero image positioned absolutely (matches About pattern) */}
        <div className="mediakit-hero-image-container">
          <img src={require('../assets/mediakit-2.jpg')} alt="Family enjoying time together on the beach" />
        </div>

        {/* Content wrapper holds the text */}
        <div className="mediakit-hero-content-wrapper">
          <div className="mediakit-hero-text">
            <p className="mediakit-tag">MEDIA KIT</p>
            <p className="mediakit-eyebrow">FINDING HOME</p>
            <h1 className="mediakit-title">lgbtPridePath.org</h1>
            <p className="mediakit-hero-body">
              LGBTPridePath.org is a free tool designed to help you make an informed decision around relocation, based on the laws and protections that are important to you, or to compare other communities with your own. Simply input your zip code, mark the legal protections and other factors important to you, and see which communities are closest to you that offer these protections. This can also include side-by-side comparisons of the area you entered to others that meet your criteria.
            </p>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* DOWNLOAD LINK                                 */}
      {/* ============================================ */}
      <section className="mediakit-download">
        <a 
          href="https://docs.google.com/document/d/1kUoFzIrQXG1awpJWTDM_X4gqzGMrRWyoWeYKoT-lfpE/edit?tab=t.0" 
          target="_blank" 
          rel="noopener noreferrer"
          className="mediakit-download-link"
        >
          download our media kit here
        </a>
      </section>

      {/* ============================================ */}
      {/* BIG PURPLE STATEMENT                          */}
      {/* ============================================ */}
      <section className="mediakit-statement">
        <p className="mediakit-statement-text">
          We are seeing a rise in conversations on the real concern of relocating to keep oneself or loved ones safe. LGBTPridePath.org is maintained and up-to-date on information critical for making a decision on relocating within the United States.
        </p>
      </section>

      {/* ============================================ */}
      {/* THREE STAT CIRCLES                            */}
      {/* ============================================ */}
      <section className="mediakit-stats">
        {/* Shared gradient definition for stat circle rings */}
        <svg width="0" height="0" style={{ position: 'absolute' }}>
          <defs>
            <linearGradient id="mediakit-ring-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#491994" />
              <stop offset="100%" stopColor="#c026d3" />
            </linearGradient>
          </defs>
        </svg>

        <div className="mediakit-stat">
          <StatCircle percentage={84} />
          <p className="mediakit-stat-text">
            84% of transgender and nonbinary people have made significant life decisions or taken steps in response to LGBTQ-related politics or laws. Including considering or actually moving to a different state; considering or finding a different job; attempting to update legal name or gender markers on identity documents; crossing state lines to receive medical care, and much more.
          </p>
          <p className="mediakit-stat-source">— Movement Advancement Project's 2025 survey</p>
        </div>

        <div className="mediakit-stat">
          <StatCircle percentage={48} />
          <p className="mediakit-stat-text">
            48% of surveyed trans adults have either relocated and/or are considering moving to another location in the US they perceive as more affirming for transgender adults.
          </p>
          <p className="mediakit-stat-source">— UCLA School of Law, Williams Institute's 2025 survey</p>
        </div>

        <div className="mediakit-stat">
          <StatCircle percentage={81} />
          <p className="mediakit-stat-text">
            81% of surveyed transgender adults living in states with anti-LGBTQ bills have moved or have began to relocate to states with less anti-trans legislation.
          </p>
          <p className="mediakit-stat-source">— Trans Collective's 2025 survey</p>
        </div>
      </section>

      {/* ============================================ */}
      {/* PHONE MOCKUP + STATEMENTS                     */}
      {/* ============================================ */}
      <section className="mediakit-phone-section">
        <div className="mediakit-phone-image">
          <img src={require('../assets/mediakit-3-video.gif')} alt="LGBT PridePath app demonstration" />
        </div>
        <div className="mediakit-phone-text">
          <h2>All of us should be able to live anywhere we want and feel safe and protected.</h2>
          <h2>Our laws in the United States should protect <em>ALL</em> of us.</h2>
          <h2>But the reality is that some cities and towns simply don't offer us equal protection.</h2>
          <p>
            With rising anti-LGBTQ+ legislation, many in our community have needed to consider relocating to another state or location. Multiple surveys have shown that there has been a growing migration since 2024 of the LGBTQ+ community, especially within the trans and nonbinary community.
          </p>
          <p>
            <a href="https://www.lgbtpridepath.org" target="_blank" rel="noopener noreferrer">LGBTPridePath.org</a> is a one-of-a-kind research tool that allows for personalized reports based on zip code, the legal protections, and other factors most important to you to find factual information when considering relocating.
          </p>
          <p>
            Factor in city population, protections by sexuality and/or gender identity, housing costs, number of LGBTQ+ resources, and distance to current location.
          </p>
          <p>
            Compare and contrast different locations, and create personalized printable results. Relocating can be a daunting task. We are here to help with highly researched and updated information that can help you make an informed decision.
          </p>
        </div>
      </section>

      {/* ============================================ */}
      {/* WHO WE ARE                                    */}
      {/* ============================================ */}
      <section className="mediakit-who">
        <div className="mediakit-who-image">
          <img src={require('../assets/mediakit-1.jpg')} alt="Family embracing" />
          <div className="mediakit-who-overlay">
            <h2>Who we are</h2>
          </div>
        </div>
        <div className="mediakit-who-text">
          <p>
            The LGBT National Help Center, a trusted non-profit serving the LGBTQ+ community providing free and confidential support through the national crisis prevention hotlines and additional online support programs, celebrating 30 years of service. <a href="https://www.lgbtpridepath.org" target="_blank" rel="noopener noreferrer">LGBTPridePath.org</a> is highly researched with the most up-to-date data and the largest resource database of LGBTQ+ resources to help ensure accurate information. Your privacy is our priority. We do not collect, store, or share personally identifiable information.
          </p>
        </div>
      </section>

      {/* ============================================ */}
      {/* CONTACT INFORMATION                           */}
      {/* ============================================ */}
      <section className="mediakit-contact">
        <div className="mediakit-contact-info">
          <h3 className="mediakit-contact-heading mediakit-heading-purple">Contact Information:</h3>
          <p>Website: <a href="https://www.lgbtpridepath.org" target="_blank" rel="noopener noreferrer">www.LGBTPridePath.org</a></p>
          <p>For General contact: <a href="mailto:LGBTPridePath@lgbthotline.org">LGBTPridePath@lgbthotline.org</a></p>
          <p>For Media Inquiries specific to LGBTPridePath: <a href="mailto:Brad@lgbthotline.org">Brad@lgbthotline.org</a></p>
          <p>For Inquiries on the LGBT National Help Center: <a href="mailto:Help@lgbthotline.org">Help@lgbthotline.org</a></p>
          <p>Main website: <a href="https://www.lgbthotline.org" target="_blank" rel="noopener noreferrer">www.LGBThotline.org</a></p>
          <p>Resource website: <a href="https://www.lgbtnearme.org" target="_blank" rel="noopener noreferrer">www.LGBTnearMe.org</a></p>

          <h3 className="mediakit-contact-heading mediakit-heading-green">Mailing Address</h3>
          <p>
            LGBT National Help Center<br />
            2261 Market Street, #296<br />
            San Francisco, CA 94114
          </p>
          <p>Office: 415-355-0003</p>

          <h3 className="mediakit-contact-heading mediakit-heading-green">Federal Tax ID#</h3>
          <p>
            13-3850982<br />
            (Listed with the IRS as the GLBT National Help Center)
          </p>
        </div>
        <div className="mediakit-contact-image">
          <img src={require('../assets/mthodolgy lower bottom.png')} alt="Community members embracing" />
        </div>
      </section>
    </div>
  );
};

export default MediaToolKit;